/**
 * Afrikaans: Refer and earn
 *
 * What this file covers: The referral programme: links, referrals, earnings and withdrawals.
 *
 * Tone: Upbeat and friendly, and honest about the terms and when money is paid.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const refer: Catalog = {
  'refer.eyebrow': 'Vennootprogram',
  'refer.title': 'Verwys en verdien',
  'refer.description': 'Stel \'n besigheid aan RepletoryReach bekend en verdien \'n deel van wat ons verdien op alles wat hulle gebruik.',
  'refer.activeDescription': 'Jy verdien {{percent}}% van wat RepletoryReach verdien op alles wat jou verwysings gebruik, so lank as wat hulle bly.',
  'refer.loadFailed': 'Jou vennootbesonderhede kon nie gelaai word nie.',
  'refer.closed': 'Die vennootprogram is op die oomblik nie oop nie. Kyk later weer.',
  'refer.closedAccount': 'Hierdie vennootrekening is gesluit.',
  'refer.suspended': 'Jou vennootrekening is op hou, so daar word niks nuuts verdien nie.',
  'refer.notEnrolled.title': 'Jy is nog nie in die vennootprogram nie',
  'refer.notEnrolled.body': 'Open Verwys en verdien om te lees hoe dit werk en aansoek te doen.',
  'refer.how.title': 'Hoe dit werk',
  'refer.how.step1': 'Doen aansoek, en ons kom terug na jou sodra iemand dit gelees het.',
  'refer.how.step2': 'Deel jou kakel of jou kode met \'n besigheid wat dit sal gebruik.',
  'refer.how.step3': 'Elke keer wat hulle die platform gebruik, verdien jy {{percent}}% van wat RepletoryReach daarop verdien, so lank as wat hulle by ons bly.',
  'refer.how.step4': 'Verdienste word na {{days}} dae beskikbaar en jy kan enigiets bo {{minimum}} na jou bank onttrek, of dit in krediete op jou eie rekening verander.',
  'refer.enrol.title': 'Doen aansoek om die program',
  'refer.enrol.description': 'Vir mense en besighede in Suid-Afrika: verdienste word in ’n Suid-Afrikaanse bankrekening betaal. ’n Paar besonderhede, en iemand lees dit en kom terug na jou, hoe dit ook al uitwerk.',
  'refer.enrol.displayName': 'Naam waaronder jy bekend wil wees',
  'refer.enrol.displayNameHint': 'Wat \'n besigheid wat jy verwys sien, en waarop ons jou in ons eie rekords noem.',
  'refer.enrol.businessName': 'Besigheidsnaam, as jy onder een handel',
  'refer.enrol.contactEmail': 'E-pos vir programboodskappe',
  'refer.enrol.contactEmailHint': 'Waar ons oor jou verdienste en jou onttrekkings skryf.',
  'refer.enrol.contactPhone': 'Foonnommer',
  'refer.enrol.website': 'Webwerf of sosiale bladsy',
  'refer.enrol.how': 'Hoe gaan jy ons bekendstel?',
  'refer.enrol.howHint': '\'n Sin of twee. Dit is wat iemand lees voor hulle jou aansoek goedkeur.',
  'refer.enrol.acceptTerms': 'Ek aanvaar die vennootvoorwaardes',
  'refer.enrol.readTerms': 'Lees die voorwaardes',
  'refer.enrol.hideTerms': 'Versteek die voorwaardes',
  'refer.enrol.version': 'weergawe {{version}}',
  'refer.enrol.submit': 'Stuur my aansoek',
  'refer.enrol.sent': 'Jou aansoek is by ons. Ons laat van ons hoor.',
  'refer.enrol.failed': 'Jou aansoek kon nie gestuur word nie.',
  'refer.pending.title': 'Jou aansoek is by ons',
  'refer.pending.body': 'Iemand lees dit en kom terug na jou. Jou kakel word uitgereik die oomblik dat dit goedgekeur is.',
  'refer.share.title': 'Jou kakel en jou kode',
  'refer.share.description': 'Enigiemand wat hierdeur registreer, word as joune aangeteken.',
  'refer.share.linkLabel': 'Jou kakel',
  'refer.share.copyCode': 'Kopieer die kode',
  'refer.share.copyLink': 'Kopieer die kakel',
  'refer.share.copied': 'Gekopieer',
  'refer.share.whatsapp': 'Deel op WhatsApp',
  'refer.share.message': 'Ek gebruik RepletoryReach vir WhatsApp met kliënte. Registreer hier en dit is binne \'n paar minute opgestel: {{link}}',
  'refer.metric.available': 'Beskikbaar om te onttrek',
  'refer.metric.availableHint': 'Deurgekom, en joune om te neem.',
  'refer.metric.pending': 'Kom nog deur',
  'refer.metric.pendingHint': 'Word {{days}} dae na die dag waarop dit verdien is, beskikbaar.',
  'refer.metric.lifetime': 'Altesaam verdien',
  'refer.metric.lifetimeHint': 'Alles wat jy ooit verdien het, voor onttrekkings.',
  'refer.metric.referrals': 'Besighede verwys',
  'refer.metric.referralsHint': 'Verdien steeds vir jou.',
  'refer.metric.withdrawn': 'Uitbetaal',
  'refer.metric.withdrawnHint': 'Wat jou reeds bereik het.',
  'refer.recent.title': 'Jou nuutste verdienste',
  'refer.recent.all': 'Sien alles',
  'refer.recent.emptyTitle': 'Nog niks verdien nie',
  'refer.recent.emptyBody': 'Deel jou kakel. Verdienste verskyn die dag nadat \'n besigheid wat jy verwys het, die platform gebruik.',
  'refer.referrals.title': 'Besighede wat jy verwys het',
  'refer.referrals.description': 'Wie deur jou geregistreer het, en wat elkeen vir jou verdien het.',
  'refer.referrals.loadFailed': 'Jou verwysings kon nie gelaai word nie.',
  'refer.referrals.emptyTitle': 'Nog geen verwysings nie',
  'refer.referrals.emptyBody': 'Deel jou kakel met \'n besigheid en hulle verskyn hier sodra hulle registreer.',
  'refer.referrals.noneMatch': 'Niks onder daardie filter nie',
  'refer.referrals.noneMatchBody': 'Probeer \'n ander een, of sien hulle almal.',
  'refer.earnings.title': 'Jou verdienste',
  'refer.earnings.description': 'Een reël per besigheid per dag, plus onttrekkings en enige regstellings.',
  'refer.earnings.loadFailed': 'Jou verdienste kon nie gelaai word nie.',
  'refer.earnings.emptyTitle': 'Nog niks hier nie',
  'refer.earnings.emptyBody': 'Verdienste verskyn die dag nadat \'n besigheid wat jy verwys het, die platform gebruik.',
  'refer.earnings.noneMatch': 'Niks onder daardie filter nie',
  'refer.earnings.noneMatchBody': 'Probeer \'n ander een, of sien hulle almal.',
  'refer.withdrawals.title': 'Onttrekkings',
  'refer.withdrawals.description': 'Neem jou verdienste na jou bank, of verander dit in krediete op hierdie rekening.',
  'refer.withdrawals.historyTitle': 'Elke onttrekking',
  'refer.withdrawals.loadFailed': 'Jou onttrekkings kon nie gelaai word nie.',
  'refer.withdrawals.emptyTitle': 'Nog niks onttrek nie',
  'refer.withdrawals.emptyBody': 'Sodra jy verdienste beskikbaar het, kan jy dit hier aanvra.',
  'refer.withdrawals.oneAtATime': 'Jy het \'n onttrekking wat wag. Sodra dit betaal of gekanselleer is, kan jy \'n volgende aanvra.',
  'refer.withdrawals.cancelTitle': 'Kanselleer hierdie onttrekking?',
  'refer.withdrawals.cancelBody': '{{amount}} gaan terug in jou beskikbare verdienste en jy kan weer aanvra wanneer jy wil.',
  'refer.withdrawals.cancelConfirm': 'Kanselleer dit',
  'refer.withdrawals.cancelled': 'Gekanselleer. Die bedrag is terug in jou beskikbare verdienste.',
  'refer.withdrawals.cancelFailed': 'Daardie onttrekking kon nie gekanselleer word nie.',
  'refer.withdraw.open': 'Onttrek',
  'refer.withdraw.title': 'Onttrek jou verdienste',
  'refer.withdraw.available': 'Jy het {{amount}} beskikbaar.',
  'refer.withdraw.amount': 'Hoeveel',
  'refer.withdraw.amountHint': 'Ten minste {{minimum}}.',
  'refer.withdraw.belowMinimum': 'Die kleinste onttrekking is {{minimum}}.',
  'refer.withdraw.tooMuch': 'Dit is meer as wat jy beskikbaar het.',
  'refer.withdraw.method': 'Hoe jy dit wil hê',
  'refer.withdraw.eft': 'Na my bankrekening',
  'refer.withdraw.eftHint': 'Betaal aan {{account}} binne tien werksdae nadat dit goedgekeur is.',
  'refer.withdraw.eftNeedsAccount': 'Voeg ’n bankrekening by onder Onttrekkings, Bankrekening, en dit word beskikbaar.',
  'refer.withdraw.credits': 'As krediete op hierdie rekening',
  'refer.withdraw.creditsHint': 'Dadelik by jou balans gevoeg, om aan boodskappe te spandeer.',
  'refer.withdraw.requestAction': 'Vra hierdie onttrekking aan',
  'refer.withdraw.convertAction': 'Verander dit in krediete',
  'refer.withdraw.requested': 'Jou onttrekking is in. Ons skryf wanneer dit betaal is.',
  'refer.withdraw.converted': 'By jou balans gevoeg.',
  'refer.withdraw.failed': 'Daardie onttrekking kon nie gemaak word nie.',
  'refer.payout.title': 'Bankrekening',
  'refer.payout.description': 'Waarheen \'n bankonttrekking betaal word. Net jy en ons rekeningspan kan dit sien.',
  'refer.payout.add': 'Voeg \'n rekening by',
  'refer.payout.change': 'Verander dit',
  'refer.payout.locked': 'Die rekening kan nie verander nie terwyl \'n onttrekking wag.',
  'refer.payout.loadFailed': 'Jou bankrekening kon nie gelaai word nie.',
  'refer.payout.emptyTitle': 'Geen rekening gestoor nie',
  'refer.payout.emptyBody': 'Voeg een by en jy kan reguit na jou bank onttrek.',
  'refer.payout.holder': 'Rekeninghouer',
  'refer.payout.holderHint': 'Die naam op die rekening, soos die bank dit het.',
  'refer.payout.bank': 'Bank',
  'refer.payout.branch': 'Takkode',
  'refer.payout.branchHint': 'Ses syfers.',
  'refer.payout.number': 'Rekeningnommer',
  'refer.payout.numberHint': 'Net syfers. Ons wys jou daarna net die laaste vier.',
  'refer.payout.type': 'Soort rekening',
  'refer.payout.typeCheque': 'Tjek of lopend',
  'refer.payout.typeSavings': 'Spaar',
  'refer.payout.typeTransmission': 'Transmissie',
  'refer.payout.saved': 'Jou bankrekening is gestoor.',
  'refer.payout.failed': 'Daardie bankbesonderhede kon nie gestoor word nie.',
  'refer.column.day': 'Dag',
  'refer.column.company': 'Besigheid',
  'refer.column.kind': 'Wat',
  'refer.column.detail': 'Besonderhede',
  'refer.column.amount': 'Bedrag',
  'refer.column.clears': 'Beskikbaar',
  'refer.column.since': 'Sedert',
  'refer.column.status': 'Status',
  'refer.column.firstUsage': 'Eerste gebruik',
  'refer.column.notYet': 'Nog nie',
  'refer.column.earned': 'Verdien',
  'refer.column.requested': 'Aangevra',
  'refer.column.method': 'Hoe betaal',
  'refer.column.reference': 'Verwysing',
  'refer.company.unnamed': '\'n Verwysde besigheid',
  'refer.cleared': 'Nou',
  'refer.filter.all': 'Alles',
  'refer.filter.earning': 'Verdien',
  'refer.filter.ended': 'Geëindig',
  'refer.filter.revoked': 'Weggeneem',
  'refer.status.earning': 'Verdien',
  'refer.status.ended': 'Geëindig',
  'refer.status.revoked': 'Weggeneem',
  'refer.kind.commission': 'Jou deel',
  'refer.kind.adjustment': 'Regstelling',
  'refer.kind.withdrawal': 'Onttrekking',
  'refer.kind.withdrawal_reversal': 'Onttrekking teruggegee',
  'refer.method.eft': 'Bankbetaling',
  'refer.method.wallet_credit': 'Krediete',
  'refer.withdrawalStatus.requested': 'Wag',
  'refer.withdrawalStatus.approved': 'Goedgekeur',
  'refer.withdrawalStatus.paid': 'Betaal',
  'refer.withdrawalStatus.rejected': 'Nie goedgekeur nie',
  'refer.withdrawalStatus.cancelled': 'Gekanselleer',
  'refer.enrol.southAfricaOnly': 'Die vennootprogram is net oop vir vennote in Suid-Afrika, omdat verdienste in ’n Suid-Afrikaanse bankrekening betaal word.',
  'refer.enrol.country': 'Waar is jy gevestig?',
  'refer.enrol.countryHint': 'Die program is vir vennote in Suid-Afrika: verdienste word in ’n Suid-Afrikaanse bankrekening betaal.',
  'refer.enrol.countryChoose': 'Kies ’n land',
  'refer.enrol.countryZa': 'Suid-Afrika',
  'refer.enrol.countryOther': 'Elders'
};

export default refer;
